import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import { RefreshCw, Search } from "lucide-react"

import { ApiError } from "@/api/client"
import {
  useRefreshTikTokBalances,
  useTikTokAccounts,
  type TikTokAccount,
} from "@/api/hooks/tiktok"
import { useAuth } from "@/auth/AuthProvider"
import { AdvertiserStatusCell } from "@/components/AdvertiserStatusCell"
import { Pagination } from "@/components/Pagination"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { NavbarTabList, navbarTabClass } from "@/components/NavbarTabs"
import {
  useNavbarLeftAction,
  useNavbarRightAction,
  usePageTitle,
} from "@/layouts/pageChrome"

import { AccountRequestDrawer } from "./AccountRequestDrawer"
import { MyRequestsPanel } from "./MyRequestsSection"
import { TopUpDrawer } from "./TopUpDrawer"

const PAGE_SIZE = 25

type MainTab = "accounts" | "requests"
type RequestSubTab = "top-up" | "accounts"
type SortableColumn = "account" | "status" | "ad_groups" | "balance"

/**
 * React port of `templates/accounts.html` (T3.1), extended with search,
 * pagination, TopUp / new-account request drawers, and My Requests.
 */

function parseBalance(raw: TikTokAccount["balance"]): number {
  if (raw === null || raw === undefined || raw === "") return 0
  const parsed = typeof raw === "number" ? raw : parseFloat(raw)
  return parsed
}

function parseBalanceForSort(raw: TikTokAccount["balance"]): number {
  const n = parseBalance(raw)
  return Number.isNaN(n) ? 0 : n
}

function hasQuotaData(quota: TikTokAccount["quota"]): boolean {
  if (!quota) return false
  return (
    (quota.total_available !== undefined && quota.total_available !== null) ||
    (quota.used !== undefined && quota.used !== null)
  )
}

function statusSortKey(status: TikTokAccount["advertiser_status"]): string {
  return (status ?? "zzz_unknown").toLowerCase()
}

function quotaRemaining(acc: TikTokAccount): number {
  if (!hasQuotaData(acc.quota)) return -1
  if (acc.quota?.remaining != null) return Number(acc.quota.remaining) || 0
  const total = Number(acc.quota?.total_available) || 0
  const used = Number(acc.quota?.used) || 0
  return total - used
}

function MoneyCell({
  amount,
  currency,
  lowThreshold = 50,
}: {
  amount: TikTokAccount["balance"]
  currency: string
  lowThreshold?: number
}) {
  const parsed = parseBalance(amount)
  const text = Number.isNaN(parsed) ? "N/A" : parsed.toFixed(2)
  const isLow = !Number.isNaN(parsed) && parsed < lowThreshold
  return (
    <td
      className="px-4 py-3 align-top text-right text-lg font-semibold"
      style={{ color: isLow ? "#f87171" : "#4ade80" }}
    >
      <span className="mr-2 text-sm font-normal text-[#b0b0b0]">{currency}</span>
      {text}
    </td>
  )
}

function SortableHeader({
  column,
  children,
  align = "left",
  sortColumn,
  sortDir,
  onSort,
}: {
  column: SortableColumn
  children: ReactNode
  align?: "left" | "right"
  sortColumn: SortableColumn
  sortDir: "asc" | "desc"
  onSort: (column: SortableColumn) => void
}) {
  const active = sortColumn === column
  return (
    <th className="cursor-pointer select-none px-0 py-0 font-semibold hover:bg-[#505050]">
      <button
        type="button"
        className={[
          "inline-flex w-full max-w-full items-center gap-1 px-4 py-3 font-semibold uppercase tracking-wide text-inherit hover:text-white",
          align === "right" ? "justify-end text-right" : "justify-start text-left",
        ].join(" ")}
        onClick={() => onSort(column)}
        aria-label={`Sort by ${typeof children === "string" ? children : column}`}
      >
        <span>{children}</span>
        {active ? (
          <span className="shrink-0 text-[0.65rem] text-[#8b9aff]" aria-hidden>
            {sortDir === "asc" ? "\u25B2" : "\u25BC"}
          </span>
        ) : null}
      </button>
    </th>
  )
}

function AccountRow({ account }: { account: TikTokAccount }) {
  const currency = account.currency ?? "USD"

  let quotaText = "N/A"
  if (hasQuotaData(account.quota)) {
    const used = account.quota?.used ?? 0
    const total = account.quota?.total_available ?? 0
    const remaining = account.quota?.remaining ?? 0
    quotaText = `${used} / ${total} (${remaining} remaining)`
  }

  return (
    <tr
      data-advertiser-id={account.advertiser_id}
      className="border-b border-[#555555] transition-colors hover:bg-[#4a4a4a]"
    >
      <td className="px-4 py-3 align-top">
        <div className="account-name text-base font-semibold text-[#e0e0e0]">
          {account.advertiser_name}
        </div>
        <div className="account-id mt-1 font-mono text-xs text-[#b0b0b0]">
          ID: {account.advertiser_id}
        </div>
      </td>
      <td className="account-status px-4 py-3 align-top text-sm font-semibold">
        <AdvertiserStatusCell status={account.advertiser_status} />
      </td>
      <td className="account-quota px-4 py-3 align-top text-sm font-semibold text-brand-accent">
        {quotaText}
      </td>
      <MoneyCell amount={account.balance} currency={currency} />
    </tr>
  )
}

export default function AccountsPage() {
  usePageTitle("", "TikTok Accounts")
  const { isAdmin } = useAuth()
  const accountsQuery = useTikTokAccounts({ includeDeleted: false })
  const refreshMutation = useRefreshTikTokBalances()

  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [sortColumn, setSortColumn] = useState<SortableColumn>("balance")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")
  const [mainTab, setMainTab] = useState<MainTab>("accounts")
  const [requestSubTab, setRequestSubTab] = useState<RequestSubTab>("top-up")
  const [topUpOpen, setTopUpOpen] = useState(false)
  const [accountRequestOpen, setAccountRequestOpen] = useState(false)

  const handleSortHeader = useCallback(
    (column: SortableColumn) => {
      if (sortColumn === column) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return
      }
      setSortColumn(column)
      setSortDir(
        column === "balance" || column === "ad_groups" ? "desc" : "asc",
      )
    },
    [sortColumn],
  )

  useEffect(() => {
    if (!successMessage) return
    const t = window.setTimeout(() => setSuccessMessage(null), 3000)
    return () => window.clearTimeout(t)
  }, [successMessage])

  useEffect(() => {
    if (!errorMessage) return
    const t = window.setTimeout(() => setErrorMessage(null), 5000)
    return () => window.clearTimeout(t)
  }, [errorMessage])

  // Surface a load error in the banner once per new error (adjusted during
  // render rather than in an effect).
  const queryError = accountsQuery.isError ? accountsQuery.error : null
  const [seenQueryError, setSeenQueryError] = useState<unknown>(null)
  if (queryError !== seenQueryError) {
    setSeenQueryError(queryError)
    if (queryError) {
      const message =
        queryError instanceof Error ? queryError.message : "Failed to load accounts"
      setErrorMessage(message)
    }
  }

  const filteredAccounts = useMemo(() => {
    const list = accountsQuery.data?.accounts ?? []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((acc) => {
      const name = (acc.advertiser_name ?? "").toLowerCase()
      const id = (acc.advertiser_id ?? "").toLowerCase()
      return name.includes(q) || id.includes(q)
    })
  }, [accountsQuery.data, search])

  const sortedAccounts = useMemo(() => {
    const mult = sortDir === "asc" ? 1 : -1
    return [...filteredAccounts].sort((a, b) => {
      switch (sortColumn) {
        case "account":
          return (
            mult *
            (a.advertiser_name ?? "").localeCompare(b.advertiser_name ?? "")
          )
        case "status":
          return (
            mult *
            statusSortKey(a.advertiser_status).localeCompare(
              statusSortKey(b.advertiser_status),
            )
          )
        case "ad_groups":
          return mult * (quotaRemaining(a) - quotaRemaining(b))
        case "balance":
          return (
            mult *
            (parseBalanceForSort(a.balance) - parseBalanceForSort(b.balance))
          )
        default:
          return 0
      }
    })
  }, [filteredAccounts, sortColumn, sortDir])

  // Reset to first page when the filter/sort set changes.
  const pageResetKey = JSON.stringify([
    search,
    sortColumn,
    sortDir,
    filteredAccounts.length,
  ])
  const [prevPageResetKey, setPrevPageResetKey] = useState(pageResetKey)
  if (pageResetKey !== prevPageResetKey) {
    setPrevPageResetKey(pageResetKey)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(sortedAccounts.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pagedAccounts = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE
    return sortedAccounts.slice(start, start + PAGE_SIZE)
  }, [sortedAccounts, safePage])

  const onRefreshClick = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)
    try {
      const data = await refreshMutation.mutateAsync()
      if (data.success) {
        const bcApplied = data.bc_budget_applied ?? 0
        const bcSuffix =
          bcApplied > 0
            ? ` (BC budgets applied: ${bcApplied})`
            : data.bc_errors?.length
              ? ` (BC budgets: none — ${data.bc_errors[0]})`
              : data.bc_count === 0
                ? " (BC budgets: no Business Centers on token)"
                : ""
        setSuccessMessage(
          `Successfully refreshed ${data.updated_count ?? 0} account(s)${bcSuffix}`,
        )
      } else {
        setErrorMessage(
          `Failed to refresh balances: ${data.message ?? data.detail ?? "Refresh failed"}`,
        )
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to refresh"
      setErrorMessage(`Failed to refresh balances: ${message}`)
    }
  }

  const isRefreshing = refreshMutation.isPending

  const refreshButton = useMemo(
    () =>
      isAdmin ? (
        <Button
          type="button"
          onClick={onRefreshClick}
          disabled={isRefreshing}
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-[#667eea] px-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#5568d3] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`}
            aria-hidden
          />
          {isRefreshing ? "Refreshing..." : "Refresh Balances"}
        </Button>
      ) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, isRefreshing],
  )
  useNavbarRightAction(refreshButton)

  const navbarTabs = useMemo(
    () => (
      <NavbarTabList>
        <button
          type="button"
          onClick={() => setMainTab("accounts")}
          className={navbarTabClass(mainTab === "accounts")}
        >
          Accounts
        </button>
        <button
          type="button"
          onClick={() => setMainTab("requests")}
          className={navbarTabClass(mainTab === "requests")}
        >
          My Requests
        </button>
      </NavbarTabList>
    ),
    [mainTab],
  )
  useNavbarLeftAction(navbarTabs)

  const sortProps = { sortColumn, sortDir, onSort: handleSortHeader }

  let listBody: React.ReactNode
  if (accountsQuery.isLoading) {
    listBody = (
      <div className="flex min-h-0 flex-1 items-center justify-center text-[#b0b0b0]">
        Loading accounts...
      </div>
    )
  } else if (accountsQuery.isError) {
    listBody = (
      <p className="flex min-h-0 flex-1 items-center justify-center text-[#f87171]">
        Error loading accounts
      </p>
    )
  } else if (!accountsQuery.data?.success || (accountsQuery.data.accounts?.length ?? 0) === 0) {
    listBody = (
      <p className="flex min-h-0 flex-1 items-center justify-center text-[#666]">
        No accounts connected yet.
      </p>
    )
  } else if (filteredAccounts.length === 0) {
    listBody = (
      <p className="flex min-h-0 flex-1 items-center justify-center text-[#666]">
        No accounts match your search.
      </p>
    )
  } else {
    listBody = (
      <div className="min-h-0 min-w-0 flex-1 overflow-auto rounded-lg border border-[#555555]">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-[#555555] bg-[#444444] text-left text-xs font-semibold uppercase tracking-wide text-[#b0b0b0]">
              <SortableHeader {...sortProps} column="account">
                Account
              </SortableHeader>
              <SortableHeader {...sortProps} column="status">
                Status
              </SortableHeader>
              <SortableHeader {...sortProps} column="ad_groups">
                Ad Groups
              </SortableHeader>
              <SortableHeader {...sortProps} column="balance" align="right">
                Balance
              </SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-[#3a3a3a]">
            {pagedAccounts.map((acc) => (
              <AccountRow key={acc.advertiser_id} account={acc} />
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#3a3a3a]">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1.5 pt-1.5 sm:px-2 sm:pt-2">
        {errorMessage ? (
          <Alert
            variant="destructive"
            className="mb-2 shrink-0 border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        {successMessage ? (
          <Alert className="mb-2 shrink-0 border-[#2d5f47] bg-[#1e4d3e] text-[#4ade80]">
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        ) : null}

        {mainTab === "accounts" ? (
          <>
            <div className="mb-1.5 flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#888]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or ID"
                  className="h-8 border-[#555] bg-[#2d2d2d] pl-9 text-sm text-white placeholder:text-[#888]"
                  aria-label="Search accounts"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={() => setTopUpOpen(true)}
                  className="h-8 bg-[#667eea] px-3 text-xs text-white hover:bg-[#5568d3]"
                >
                  TopUp
                </Button>
                <Button
                  type="button"
                  onClick={() => setAccountRequestOpen(true)}
                  className="h-8 bg-[#4a5568] px-3 text-xs text-white hover:bg-[#3d4654]"
                >
                  Request new ad account
                </Button>
              </div>
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {listBody}
            </div>
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-[#e0e0e0]">
                My Requests
              </h2>
              <p className="mt-1 text-sm text-[#888]">
                Track top-up and new ad account requests and their status.
              </p>
            </div>
            <MyRequestsPanel
              subTab={requestSubTab}
              onSubTabChange={setRequestSubTab}
            />
          </div>
        )}
      </div>

      {mainTab === "accounts" && sortedAccounts.length > 0 ? (
        <Pagination
          className="border-t border-[#4a4a4a] bg-[#303030] px-2 py-2 sm:px-3"
          page={safePage}
          total={sortedAccounts.length}
          pageSize={PAGE_SIZE}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      ) : null}

      <TopUpDrawer
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        onSubmitted={() => {
          setSuccessMessage("Top-up request submitted")
          setMainTab("requests")
          setRequestSubTab("top-up")
        }}
      />
      <AccountRequestDrawer
        open={accountRequestOpen}
        onClose={() => setAccountRequestOpen(false)}
        onSubmitted={() => {
          setSuccessMessage("Ad account request submitted")
          setMainTab("requests")
          setRequestSubTab("accounts")
        }}
      />
    </div>
  )
}
